import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Plus, X, ShieldCheck, ShieldAlert } from "lucide-react";

const empty = { name: "", license_type: "", issuing_authority: "", license_number: "", issue_date: "", expiry_date: "", client_id: "", notes: "" };

export default function Licenses() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientMap, setClientMap] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);

  const load = () => api.get("/licenses").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/clients").then((r) => {
      setClients(r.data);
      const m = {};
      r.data.forEach((c) => (m[c.id] = c.name));
      setClientMap(m);
    });
  }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, client_id: form.client_id || null };
      if (editingId) {
        await api.put(`/licenses/${editingId}`, payload);
        toast.success("License updated");
      } else {
        await api.post("/licenses", payload);
        toast.success("License added");
      }
      setForm(empty); setEditingId(null); setShowForm(false); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const edit = (l) => { setForm({ ...empty, ...l, client_id: l.client_id || "" }); setEditingId(l.id); setShowForm(true); };
  const remove = async (l) => {
    if (!window.confirm(`Delete ${l.name}?`)) return;
    await api.delete(`/licenses/${l.id}`);
    toast.success("Deleted"); load();
  };

  const pillFor = (l) => {
    if (l.status === "expired") return { cls: "error", label: "Expired" };
    if (l.status === "expiring_soon") return { cls: "warn", label: `${l.days_to_expiry}d left` };
    return { cls: "success", label: "Active" };
  };

  return (
    <div>
      <PageHeader
        eyebrow="Compliance"
        title="Licenses tracker"
        subtitle="Environmental permits and renewals for HBT and its clients."
        actions={user?.role === "admin" && (
          <button onClick={() => { setShowForm(true); setEditingId(null); setForm(empty); }} className="btn-primary inline-flex items-center gap-2" data-testid="add-license-btn">
            <Plus className="w-4 h-4" /> Add license
          </button>
        )}
      />

      <div className="px-8 lg:px-12 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 stagger">
          {items.map((l) => {
            const p = pillFor(l);
            return (
              <div key={l.id} className="hbt-card p-6" data-testid={`license-card-${l.id}`}>
                <div className="flex items-start justify-between">
                  <div className="w-10 h-10 rounded-md grid place-items-center" style={{ background: l.status === "expired" ? "rgba(155,34,38,0.08)" : "rgba(26,54,45,0.08)", color: l.status === "expired" ? "var(--error)" : "var(--forest)" }}>
                    {l.status === "expired" || l.status === "expiring_soon" ? <ShieldAlert className="w-5 h-5" /> : <ShieldCheck className="w-5 h-5" />}
                  </div>
                  <span className={`pill ${p.cls}`}>{p.label}</span>
                </div>
                <div className="mt-5">
                  <div className="font-display text-lg font-semibold tracking-tight leading-tight">{l.name}</div>
                  <div className="text-xs text-neutral-500 mt-1">{l.license_type}</div>
                </div>
                <div className="mt-5 space-y-1 text-xs">
                  <div><span className="text-neutral-400">Authority · </span>{l.issuing_authority || "—"}</div>
                  <div><span className="text-neutral-400">Number · </span><span className="font-mono">{l.license_number || "—"}</span></div>
                  <div><span className="text-neutral-400">Expires · </span><span className="font-mono">{l.expiry_date}</span></div>
                  <div><span className="text-neutral-400">Scope · </span>{l.client_id ? (clientMap[l.client_id] || "Client") : "HBT internal"}</div>
                </div>
                {user?.role === "admin" && (
                  <div className="mt-5 flex items-center gap-2">
                    <button onClick={() => edit(l)} className="btn-outline text-xs" data-testid={`edit-license-${l.id}`}>Edit</button>
                    <button onClick={() => remove(l)} className="btn-outline text-xs" data-testid={`delete-license-${l.id}`}>Delete</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {items.length === 0 && <div className="hbt-card p-16 text-center text-neutral-500">No licenses tracked yet.</div>}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="hbt-card p-8 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="license-form-modal">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="hbt-label">{editingId ? "Edit" : "New"}</div>
                <div className="font-display text-2xl font-semibold mt-1">{editingId ? "Update license" : "Add a license"}</div>
              </div>
              <button onClick={() => setShowForm(false)} className="text-neutral-400 hover:text-black"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={save} className="space-y-4">
              <div>
                <label className="hbt-label block mb-2">Name</label>
                <input required className="hbt-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="license-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="hbt-label block mb-2">Type</label>
                  <input required className="hbt-input" placeholder="Environmental" value={form.license_type} onChange={(e) => setForm({ ...form, license_type: e.target.value })} data-testid="license-type-input" />
                </div>
                <div>
                  <label className="hbt-label block mb-2">Number</label>
                  <input className="hbt-input" value={form.license_number || ""} onChange={(e) => setForm({ ...form, license_number: e.target.value })} data-testid="license-number-input" />
                </div>
              </div>
              <div>
                <label className="hbt-label block mb-2">Issuing authority</label>
                <input className="hbt-input" value={form.issuing_authority || ""} onChange={(e) => setForm({ ...form, issuing_authority: e.target.value })} data-testid="license-authority-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="hbt-label block mb-2">Issue date</label>
                  <input type="date" className="hbt-input" value={form.issue_date || ""} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} data-testid="license-issue-input" />
                </div>
                <div>
                  <label className="hbt-label block mb-2">Expiry date</label>
                  <input type="date" required className="hbt-input" value={form.expiry_date || ""} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} data-testid="license-expiry-input" />
                </div>
              </div>
              <div>
                <label className="hbt-label block mb-2">Scope</label>
                <select className="hbt-input" value={form.client_id || ""} onChange={(e) => setForm({ ...form, client_id: e.target.value })} data-testid="license-scope-select">
                  <option value="">HBT internal</option>
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="hbt-label block mb-2">Notes</label>
                <textarea rows={2} className="hbt-input" value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="license-notes-input" />
              </div>
              <div className="flex justify-end gap-2 pt-3">
                <button type="button" onClick={() => setShowForm(false)} className="btn-outline">Cancel</button>
                <button type="submit" className="btn-primary" data-testid="save-license">{editingId ? "Update" : "Create"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
